/**
 * Exact in-product consent and notice text for the October 16, 2026 sweep.
 * Web and the iOS build both read these strings. Placeholders are filled
 * from account data. The stored notice is the filled text.
 */

const DEFAULT_TRIAL_DAYS = 14;
const {
  VENDOR_TEXT_VERSION,
  vendorBox1,
  vendorBox2,
  vendorBox4,
  vendorBox5,
  vendorBoxes,
  validateVendorConsents,
  vendorConsentLog,
  publicVendorDiscount,
} = require('./vendorApplication');
const { addTrialDays, formatTrialLongDate, trialDisplayTimeZone } = require('./trialStatus');
const { normalizeAddon, seatMonthlyTotal } = require('./planLimits');
const { checkoutPlanLabel, isTeamPlanName } = require('./planNames');

const TERMS_EFFECTIVE_LABEL = 'October 13, 2026';
const TERMS_DOCUMENT_VERSION = '2026-10-22';
const TEXT_VERSION = '2026-10-16';
const HOMEOWNER_INVITE_TEXT_VERSION = '2026-10-16.homeowner-invite.v1';
const TERMS_UPDATE_TEXT_VERSION = '2026-10-22.terms-update.v1';
const TRIAL_TEXT_VERSION = '2026-10-17.trial.v3';
const SUBSCRIPTION_TEXT_VERSION = '2026-10-17.subscription.v3';

const HOMEOWNER_TERMS = 'homeowner_terms';
const TERMS_UPDATE = 'terms_update';
const TRIAL_AUTORENEWAL = 'trial_autorenewal';
const SUBSCRIPTION_AUTORENEWAL = 'subscription_autorenewal';
const REFUND_FIRST_CHARGE = 'first_charge';
const REFUND_PRIOR_CHARGE = 'prior_charge';
const SUBSCRIBE_CANCEL_LEAD = 'Cancel anytime online in Settings → Subscription & Billing.';
const SUBSCRIBE_NOTICE_LINKS = 'Auto-Renewal Terms (/trial-terms) · Refund & Cancellation Policy (/refunds) · Terms of Service (/terms)';
const PAID_HISTORY_STATUSES = new Set([
  'active',
  'past_due',
  'unpaid',
  'incomplete',
  'incomplete_expired',
]);

const SOLO_PRICE = 49;
const TEAM_PRICES = {
  2: 98,
  3: 133,
  4: 168,
  5: 203,
  6: 238,
  7: 273,
  8: 308,
  9: 343,
  10: 378,
};

const HOMEOWNER_CONSENT_ERROR =
  "Please confirm you're 18+ and agree to the Terms of Service and Privacy Policy to continue.";

const HOMEOWNER_BUTTON = 'Agree & Create Account';
const TERMS_UPDATE_BUTTON = 'Continue';

const TERMS_UPDATE_SUMMARY =
  "We've updated our Terms of Service and Privacy Policy, effective October 13, 2026, and last updated October 22, 2026. Key changes: handing a home's history to the buyer after closing, including the seller's consent, what is copied, phone numbers and email addresses left out of the buyer's copy, the buyer choosing their own sponsor, and claim links that expire after 30 days.";

const AGENT_AI_NOTICE =
  "Answers are generated by AI (provided by Anthropic) and can be wrong. They're not professional advice. For safety issues (gas, electrical, structural, mold, fire, carbon monoxide), contact a licensed pro or call 911.";

const VENDOR_SUBMIT_BUTTON = 'Submit my business';
const VENDOR_BEFORE_HEADING = 'Before you submit';
const VENDOR_SMALL_PRINT =
  'Pacarion may review, edit for formatting, decline, or remove any listing at any time. Your business information will be shown to homeowners who use Pacarion. Pacarion LLC, PO Box 530213, DeBary, FL 32753, support@pacarion.com.';

const FLORIDA_EARLY_PAY_SOURCE = 'https://www.flsenate.gov/Laws/Statutes/2025/197.162';
const FLORIDA_EARLY_PAY_TITLE = 'Property tax early-pay discount';
const FLORIDA_EARLY_PAY_NOTICE =
  'Florida property taxes get a 4% discount if paid in November (or within 30 days after your tax bill is mailed, if later). The discount drops by 1% each month through February. Check your county tax collector for your exact dates.';
const FLORIDA_EARLY_PAY_SOURCE_LABEL = 's. 197.162, Fla. Stat.';
const FLORIDA_EARLY_PAY_CHECKED = 'Last checked: Oct 16, 2026';

const REGIONAL_VENDORS_ENABLED = false;

function cleanName(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function agentNameParts(agent) {
  return {
    first: cleanName(agent && (agent.firstName || agent.first_name)),
    last: cleanName(agent && (agent.lastName || agent.last_name)),
  };
}

function agentNameKnown(agent) {
  return Boolean(agentNameParts(agent).first);
}

function homeownerInviteCopy(agent) {
  const parts = agentNameParts(agent);
  const known = Boolean(parts.first);
  const full = known ? [parts.first, parts.last].filter(Boolean).join(' ') : 'Your sponsor';
  const mid = known ? parts.first : 'your sponsor';
  return {
    nameKnown: known,
    intro: `${full} invited you to Pacarion, a home-maintenance app.`,
    sharing: `While you're connected, ${mid} can see your property details, tasks, uploaded photos and documents, service pros you add, and which vendors you contact. Your AI Assistant conversations are private: ${mid} can't see them.`,
    control: 'You control your home records and can export or delete them anytime.',
  };
}

function homeownerInviteNotice(agent) {
  const copy = homeownerInviteCopy(agent);
  return `${copy.intro} ${copy.sharing} ${copy.control}`;
}

function homeownerCheckboxPlain() {
  return 'I am 18 or older and agree to the Terms of Service (including the arbitration agreement and class action waiver, which I may opt out of within 30 days), and I have read the Privacy Policy.';
}

function termsUpdateCheckboxPlain() {
  return 'I agree to the updated Terms of Service and have read the Privacy Policy.';
}

function termsUpdateNotice() {
  return `${TERMS_UPDATE_SUMMARY} View changes. ${termsUpdateCheckboxPlain()}`;
}

function homeownerAiNoticePlain() {
  return "About the AI Assistant: answers are generated by AI (provided by Anthropic) and can be wrong. They're not professional advice. Your questions and any photos you submit are sent to our AI provider to generate a response and are saved in your Pacarion account. Your AI conversations are private to you. Your sponsor can't see them. (Pacarion may access them only as needed to operate, secure, and support the service.) Don't include sensitive personal information. For safety issues (gas, electrical, structural, mold, fire, carbon monoxide), contact a licensed pro or call 911.";
}

function formatMoney(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '0.00';
  return value.toFixed(2);
}

function priceForAgents(agents) {
  const count = Number(agents);
  if (count > 1 && Object.prototype.hasOwnProperty.call(TEAM_PRICES, count)) return TEAM_PRICES[count];
  return SOLO_PRICE;
}

function planDisplayName(planName, agents) {
  return checkoutPlanLabel(planName, agents);
}

function formatLongDate(value, timeZone) {
  return formatTrialLongDate(value, timeZone);
}

function trialEndFor({ profile, now = new Date() }) {
  const status = String((profile && profile.subscription_status) || '').toLowerCase();
  if (status === 'trialing' && profile.trial_ends_at) {
    const existing = new Date(profile.trial_ends_at);
    if (!Number.isNaN(existing.getTime())) {
      return { date: existing, alreadyTrialing: true };
    }
  }
  const at = now instanceof Date ? now : new Date(now);
  return { date: addTrialDays(at, DEFAULT_TRIAL_DAYS), alreadyTrialing: false };
}

function addonKeptOnSubscriptionChange(planName, addon) {
  if (!isTeamPlanName(planName)) return 'none';
  return normalizeAddon(addon);
}

function trialOffer({ planName, agents, profile, now, timeZone, addon } = {}) {
  const end = trialEndFor({ profile, now });
  const zone = trialDisplayTimeZone(timeZone);
  const team = isTeamPlanName(planName);
  const count = team ? Math.max(2, Math.floor(Number(agents) || 2)) : 1;
  const base = priceForAgents(team ? count : 1);
  const normalized = normalizeAddon(addon);
  const total = seatMonthlyTotal(base, count, normalized);
  const addonLabel = normalized === 'plus' ? 'Plus' : (normalized === 'max' ? 'Max' : '');
  return {
    planName: checkoutPlanLabel(planName, count),
    price: total,
    priceText: formatMoney(total),
    addon: normalized,
    addonLabel,
    trialEnd: end.date,
    trialEndLabel: formatLongDate(end.date, zone),
    alreadyTrialing: end.alreadyTrialing,
    trialDays: DEFAULT_TRIAL_DAYS,
  };
}

function trialLead(offer) {
  if (offer.alreadyTrialing) {
    return `Your free trial ends on ${offer.trialEndLabel}. A card is required.`;
  }
  return 'Your 14-day free trial starts today. A card is required.';
}

function trialChargeSentence(offer) {
  const addonBit = offer && offer.addonLabel ? `, including the ${offer.addonLabel} add-on` : '';
  return `On ${offer.trialEndLabel}, your card will be charged $${offer.priceText} for the ${offer.planName} plan${addonBit}, and then every month at the then-current price, until you cancel.`;
}

function trialRefundSentence() {
  return 'Refunds: you can request a full refund of your first charge within 7 days of it, which also cancels your subscription. After that, cancellation takes effect at the end of the billing period, with no prorated refunds except as required by law.';
}

function trialReminderSentence() {
  return "We'll email you reminders 5 days and 1 day before your first charge.";
}

function trialCancelSentence(offer) {
  return `Cancel anytime online in Settings → Subscription & Billing. You won't be charged if you cancel before ${offer.trialEndLabel}. ${trialRefundSentence()}`;
}

function trialCheckboxPlain(offer) {
  return `I agree that after my 14-day free trial ends on ${offer.trialEndLabel}, Pacarion will automatically charge my card $${offer.priceText}, and then every month at the then-current price, until I cancel. I can cancel anytime online.`;
}

function trialNoticePlain(offer) {
  return [
    trialLead(offer),
    trialChargeSentence(offer),
    trialReminderSentence(),
    trialCancelSentence(offer),
    SUBSCRIBE_NOTICE_LINKS,
    trialCheckboxPlain(offer),
  ].join(' ');
}

function trialButtonLabel(offer) {
  return `Start Free Trial: $0 today, then $${offer.priceText}/mo`;
}

function subscribeOffer({ planName, agents, addon } = {}) {
  const team = isTeamPlanName(planName);
  const count = team ? Math.max(2, Math.floor(Number(agents) || 2)) : 1;
  const base = priceForAgents(team ? count : 1);
  const normalized = normalizeAddon(addon);
  const total = seatMonthlyTotal(base, count, normalized);
  const addonLabel = normalized === 'plus' ? 'Plus' : (normalized === 'max' ? 'Max' : '');
  const label = checkoutPlanLabel(planName, count);
  return {
    planName: label,
    planLabel: label,
    agents: count,
    addon: normalized,
    addonLabel,
    price: total,
    priceText: formatMoney(total),
  };
}

function subscribeChargeSentence(offer) {
  const addonBit = offer && offer.addonLabel ? `, including the ${offer.addonLabel} add-on` : '';
  return `Your card will be charged $${offer.priceText} today for the ${offer.planLabel} plan${addonBit}, and then every month at the then-current price, until you cancel.`;
}

function normalizeRefundVariant(value) {
  return value === REFUND_FIRST_CHARGE ? REFUND_FIRST_CHARGE : REFUND_PRIOR_CHARGE;
}

function subscribeRefundSentence(variant) {
  if (normalizeRefundVariant(variant) === REFUND_FIRST_CHARGE) {
    return 'Refunds: you can request a full refund of this first charge within 7 days of it, which also cancels your subscription. After that, cancellation takes effect at the end of the billing period, with no prorated refunds except as required by law.';
  }
  return "Refunds: cancellation takes effect at the end of the billing period. We don't provide prorated or partial-month refunds, except as required by law or for charges made in error.";
}

/**
 * Profile and team rows that already show a paid subscription are 'paid'.
 * A team link we could not load is 'unknown'. Anything else is 'clear'.
 */
function accountPaidHistory({ profile, team, teamOwner, teamResolved } = {}) {
  const paid = [profile, teamOwner, team].some((row) => (
    PAID_HISTORY_STATUSES.has(String((row && row.subscription_status) || '').toLowerCase())
  ));
  if (paid) return 'paid';
  const linked = Boolean(profile && (profile.had_team_access === true || profile.prior_team_id || profile.team_id));
  if (linked && teamResolved !== true) return 'unknown';
  return 'clear';
}

/** stripePaid null means the Stripe lookup did not answer. Unsure is a prior charge. */
function refundVariantFromSignals({ history, stripePaid } = {}) {
  if (history === 'paid' || history === 'unknown' || stripePaid === true || stripePaid == null) {
    return REFUND_PRIOR_CHARGE;
  }
  return REFUND_FIRST_CHARGE;
}

function displayedRefundVariant(profile, remote) {
  const remoteVariant = remote && remote.refundVariant;
  if (remoteVariant === REFUND_FIRST_CHARGE || remoteVariant === REFUND_PRIOR_CHARGE) return remoteVariant;
  const linked = Boolean(profile && (profile.had_team_access === true || profile.team_id || profile.prior_team_id));
  return refundVariantFromSignals({
    history: accountPaidHistory({ profile, teamResolved: !linked }),
    stripePaid: profile && profile.stripe_customer_id ? null : false,
  });
}

function subscribeCheckboxPlain(offer) {
  return `I agree that Pacarion will charge my card $${offer.priceText} today, and then every month at the then-current price, until I cancel. I can cancel anytime online.`;
}

function subscribeNoticePlain(offer, variant) {
  return [
    subscribeChargeSentence(offer),
    SUBSCRIBE_CANCEL_LEAD,
    subscribeRefundSentence(variant),
    SUBSCRIBE_NOTICE_LINKS,
    subscribeCheckboxPlain(offer),
  ].join(' ');
}

function subscribeButtonLabel(offer) {
  return `Subscribe to ${offer.planLabel}: $${offer.priceText}/mo`;
}

function trialConsentApplies({ profile, checkout }) {
  const status = String((profile && profile.subscription_status) || '').toLowerCase();
  if (status === 'trialing' && profile.trial_ends_at) return true;
  return checkout === true;
}

function homeownerDiscountLabel(vendorName) {
  return `Discount offered by ${cleanName(vendorName) || 'this vendor'}.`;
}

function isFloridaHome(state) {
  const value = String(state || '').trim().toLowerCase();
  return value === 'fl' || value === 'florida';
}

function isEarlyPayTaxItem(item) {
  return String((item && item.title) || '').trim().toLowerCase() === FLORIDA_EARLY_PAY_TITLE.toLowerCase();
}

function showEarlyPayTax(item, state) {
  return isEarlyPayTaxItem(item) && isFloridaHome(state);
}

function homeownerGate(client, termsAccepted) {
  if (!client) return 'app';
  if (!client.has_password && !client.password_reset_required) return 'invite';
  if (termsAccepted === true) return 'app';
  if (termsAccepted === false) return 'update';
  return 'loading';
}

function termsAcceptedFromRows(rows) {
  return (rows || []).some((row) => (
    row
    && (row.consent_type === HOMEOWNER_TERMS || row.consent_type === TERMS_UPDATE)
    && row.document_versions === TERMS_DOCUMENT_VERSION
    && row.checkbox_state
    && row.checkbox_state.accepted === true
  ));
}

function consentActorId(client) {
  if (!client) return null;
  return client.user_id || client.id || null;
}

function homeownerConsentRow({
  client,
  agent,
  kind,
  ip,
  userAgent,
  now = new Date(),
}) {
  const userId = consentActorId(client);
  if (!userId) return { error: 'missing_actor' };
  const acceptedAt = (now instanceof Date ? now : new Date(now)).toISOString();
  const invite = kind === 'invite';
  if (invite && !agentNameKnown(agent)) return { error: 'missing_agent' };
  const notice = invite ? homeownerInviteNotice(agent) : termsUpdateNotice();
  const checkbox = invite ? homeownerCheckboxPlain() : termsUpdateCheckboxPlain();
  const row = {
    user_id: userId,
    role: 'homeowner',
    consent_type: invite ? HOMEOWNER_TERMS : TERMS_UPDATE,
    document_versions: TERMS_DOCUMENT_VERSION,
    text_version: invite ? HOMEOWNER_INVITE_TEXT_VERSION : TERMS_UPDATE_TEXT_VERSION,
    notice_text: `${notice} ${checkbox}`.slice(0, 4000),
    email: String(client.email || '').trim().toLowerCase() || null,
    checkbox_state: { accepted: true },
    accepted_at: acceptedAt,
    ip: ip ? String(ip).slice(0, 80) : null,
    user_agent: userAgent ? String(userAgent).slice(0, 500) : null,
  };
  if (invite) row.invite_code = cleanName(client.invite_code) || null;
  return { row };
}

function trialConsentRow({
  userId,
  email,
  offer,
  ip,
  userAgent,
  stripeCustomerId,
  stripeSubscriptionId,
  now = new Date(),
}) {
  if (!userId) return { error: 'missing_actor' };
  if (!offer || !offer.trialEndLabel || !offer.priceText) return { error: 'missing_offer' };
  const acceptedAt = (now instanceof Date ? now : new Date(now)).toISOString();
  return {
    row: {
      user_id: userId,
      role: 'agent',
      consent_type: TRIAL_AUTORENEWAL,
      document_versions: `${TERMS_DOCUMENT_VERSION};trial-terms;refunds;terms`,
      text_version: TRIAL_TEXT_VERSION,
      notice_text: trialNoticePlain(offer).slice(0, 4000),
      email: String(email || '').trim().toLowerCase() || null,
      checkbox_state: { autoRenewal: true, disclosureShown: true },
      accepted_at: acceptedAt,
      ip: ip ? String(ip).slice(0, 80) : null,
      user_agent: userAgent ? String(userAgent).slice(0, 500) : null,
      plan: offer.planName,
      price: offer.priceText,
      trial_ends_at: offer.trialEnd.toISOString(),
      stripe_customer_id: stripeCustomerId || null,
      stripe_subscription_id: stripeSubscriptionId || null,
    },
  };
}

function trialConsentAccepted(body) {
  return Boolean(body && body.consent && body.consent.autoRenewal === true);
}

const HOMEOWNER_PLAN_TEXT_VERSION = '2026-10-26.homeowner-plan.v3';
const HOMEOWNER_CANCEL_PATH = 'More → Subscription → Cancel plan, in the app or at app.pacarion.com/homeowner/plan';
const HOMEOWNER_PLAN_MONTHLY = { cents: 499, priceText: '4.99', interval: 'month', every: 'every month' };
const HOMEOWNER_PLAN_ANNUAL = { cents: 4900, priceText: '49.00', interval: 'year', every: 'every year' };

function homeownerPlanOffer(interval, now) {
  const plan = interval === 'year' ? HOMEOWNER_PLAN_ANNUAL : HOMEOWNER_PLAN_MONTHLY;
  const start = now instanceof Date ? now : new Date(now || Date.now());
  const renewal = new Date(start.getTime());
  renewal.setUTCFullYear(renewal.getUTCFullYear() + 1);
  return {
    planLabel: plan.interval === 'year' ? 'Homeowner annual' : 'Homeowner monthly',
    priceText: plan.priceText,
    price: plan.interval === 'year' ? 49 : 4.99,
    interval: plan.interval,
    every: plan.every,
    renewalLabel: formatLongDate(renewal, 'America/New_York'),
  };
}

function homeownerPlanChargeSentence(offer) {
  if (offer && offer.interval === 'year') {
    const when = offer.renewalLabel || 'the same date next year';
    return `Your card will be charged $${offer.priceText} today and every year on ${when} until you cancel. We'll remind you 35 and 7 days before each renewal.`;
  }
  return `Your card will be charged $${offer.priceText} today and every month until you cancel.`;
}

function homeownerPlanCheckboxPlain(offer) {
  const cadence = offer && offer.interval === 'year'
    ? `$${offer.priceText} every year`
    : `$${offer.priceText} every month`;
  return `I agree that Pacarion will charge my card ${cadence} until I cancel, and I agree to the Auto-Renewal Terms and Refund Policy.`;
}

function homeownerPlanCancelSentence(offer) {
  const annual = offer && offer.interval === 'year';
  const refund = annual
    ? 'Full refund of your first charge if requested within 7 days, and of each renewal charge within 7 days after it.'
    : 'Full refund of your first charge if requested within 7 days.';
  return `Cancel anytime online in ${HOMEOWNER_CANCEL_PATH}. You keep access through the end of your paid period. ${refund} No prorated refunds. No free trial.`;
}

function homeownerPlanButtonLabel(offer) {
  if (offer && offer.interval === 'year') return 'Subscribe: $49/year';
  return 'Subscribe: $4.99/month';
}

function homeownerPlanNoticePlain(offer) {
  return [
    homeownerPlanChargeSentence(offer),
    homeownerPlanCancelSentence(offer),
    SUBSCRIBE_NOTICE_LINKS,
    homeownerPlanCheckboxPlain(offer),
  ].join(' ');
}

function homeownerSignupConsentRow({
  client,
  ip,
  userAgent,
  now = new Date(),
}) {
  const userId = consentActorId(client);
  if (!userId) return { error: 'missing_actor' };
  const acceptedAt = (now instanceof Date ? now : new Date(now)).toISOString();
  const checkbox = homeownerCheckboxPlain();
  return {
    row: {
      user_id: userId,
      role: 'homeowner',
      consent_type: HOMEOWNER_TERMS,
      document_versions: TERMS_DOCUMENT_VERSION,
      text_version: HOMEOWNER_INVITE_TEXT_VERSION,
      notice_text: checkbox.slice(0, 4000),
      email: String(client.email || '').trim().toLowerCase() || null,
      checkbox_state: { accepted: true },
      accepted_at: acceptedAt,
      ip: ip ? String(ip).slice(0, 80) : null,
      user_agent: userAgent ? String(userAgent).slice(0, 500) : null,
      invite_code: cleanName(client.invite_code) || null,
    },
  };
}

function homeownerPlanConsentRow({
  userId,
  email,
  offer,
  ip,
  userAgent,
  stripeCustomerId,
  refundVariant,
  now = new Date(),
}) {
  if (!userId) return { error: 'missing_actor' };
  if (!offer || !offer.priceText || !offer.planLabel) return { error: 'missing_offer' };
  const variant = normalizeRefundVariant(refundVariant);
  const acceptedAt = (now instanceof Date ? now : new Date(now)).toISOString();
  return {
    row: {
      user_id: userId,
      role: 'homeowner',
      consent_type: SUBSCRIPTION_AUTORENEWAL,
      document_versions: `${TERMS_DOCUMENT_VERSION};trial-terms;refunds;terms`,
      text_version: HOMEOWNER_PLAN_TEXT_VERSION,
      notice_text: homeownerPlanNoticePlain(offer, variant).slice(0, 4000),
      email: String(email || '').trim().toLowerCase() || null,
      checkbox_state: {
        autoRenewal: true,
        disclosureShown: true,
        chargeToday: true,
        refundVariant: variant,
      },
      accepted_at: acceptedAt,
      ip: ip ? String(ip).slice(0, 80) : null,
      user_agent: userAgent ? String(userAgent).slice(0, 500) : null,
      plan: offer.planLabel,
      price: offer.priceText,
      refund_variant: variant,
      stripe_customer_id: stripeCustomerId || null,
    },
  };
}

function consentKind(body) {
  const kind = body && body.consent && body.consent.kind;
  return kind === 'trial' || kind === 'subscribe' ? kind : '';
}

function subscribeConsentRow({
  userId,
  email,
  offer,
  ip,
  userAgent,
  stripeCustomerId,
  refundVariant,
  now = new Date(),
}) {
  if (!userId) return { error: 'missing_actor' };
  if (!offer || !offer.priceText || !offer.planLabel) return { error: 'missing_offer' };
  const variant = normalizeRefundVariant(refundVariant);
  const acceptedAt = (now instanceof Date ? now : new Date(now)).toISOString();
  return {
    row: {
      user_id: userId,
      role: 'agent',
      consent_type: SUBSCRIPTION_AUTORENEWAL,
      document_versions: `${TERMS_DOCUMENT_VERSION};trial-terms;refunds;terms`,
      text_version: SUBSCRIPTION_TEXT_VERSION,
      notice_text: subscribeNoticePlain(offer, variant).slice(0, 4000),
      email: String(email || '').trim().toLowerCase() || null,
      checkbox_state: {
        autoRenewal: true,
        disclosureShown: true,
        chargeToday: true,
        refundVariant: variant,
      },
      accepted_at: acceptedAt,
      ip: ip ? String(ip).slice(0, 80) : null,
      user_agent: userAgent ? String(userAgent).slice(0, 500) : null,
      plan: offer.planLabel,
      price: offer.priceText,
      refund_variant: variant,
      stripe_customer_id: stripeCustomerId || null,
      stripe_subscription_id: null,
    },
  };
}

module.exports = {
  DEFAULT_TRIAL_DAYS,
  TERMS_EFFECTIVE_LABEL,
  TERMS_DOCUMENT_VERSION,
  TEXT_VERSION,
  HOMEOWNER_INVITE_TEXT_VERSION,
  TERMS_UPDATE_TEXT_VERSION,
  TRIAL_TEXT_VERSION,
  SUBSCRIPTION_TEXT_VERSION,
  VENDOR_TEXT_VERSION,
  HOMEOWNER_TERMS,
  TERMS_UPDATE,
  TRIAL_AUTORENEWAL,
  SUBSCRIPTION_AUTORENEWAL,
  REFUND_FIRST_CHARGE,
  REFUND_PRIOR_CHARGE,
  SUBSCRIBE_CANCEL_LEAD,
  SUBSCRIBE_NOTICE_LINKS,
  SOLO_PRICE,
  TEAM_PRICES,
  HOMEOWNER_CONSENT_ERROR,
  HOMEOWNER_BUTTON,
  TERMS_UPDATE_BUTTON,
  TERMS_UPDATE_SUMMARY,
  AGENT_AI_NOTICE,
  VENDOR_SUBMIT_BUTTON,
  VENDOR_BEFORE_HEADING,
  VENDOR_SMALL_PRINT,
  FLORIDA_EARLY_PAY_SOURCE,
  FLORIDA_EARLY_PAY_SOURCE_LABEL,
  FLORIDA_EARLY_PAY_TITLE,
  FLORIDA_EARLY_PAY_NOTICE,
  FLORIDA_EARLY_PAY_CHECKED,
  REGIONAL_VENDORS_ENABLED,
  agentNameKnown,
  homeownerInviteCopy,
  homeownerInviteNotice,
  homeownerCheckboxPlain,
  termsUpdateCheckboxPlain,
  termsUpdateNotice,
  homeownerAiNoticePlain,
  formatMoney,
  priceForAgents,
  planDisplayName,
  formatLongDate,
  trialEndFor,
  addonKeptOnSubscriptionChange,
  trialOffer,
  trialLead,
  trialChargeSentence,
  trialRefundSentence,
  trialReminderSentence,
  trialCancelSentence,
  trialCheckboxPlain,
  trialNoticePlain,
  trialButtonLabel,
  subscribeOffer,
  subscribeChargeSentence,
  subscribeRefundSentence,
  normalizeRefundVariant,
  accountPaidHistory,
  refundVariantFromSignals,
  displayedRefundVariant,
  subscribeCheckboxPlain,
  subscribeNoticePlain,
  subscribeButtonLabel,
  subscribeConsentRow,
  consentKind,
  trialConsentApplies,
  vendorBox1,
  vendorBox2,
  vendorBox4,
  vendorBox5,
  vendorBoxes,
  validateVendorConsents,
  vendorConsentLog,
  homeownerDiscountLabel,
  publicVendorDiscount,
  isFloridaHome,
  isEarlyPayTaxItem,
  showEarlyPayTax,
  homeownerGate,
  termsAcceptedFromRows,
  consentActorId,
  homeownerConsentRow,
  trialConsentRow,
  trialConsentAccepted,
  HOMEOWNER_PLAN_TEXT_VERSION,
  homeownerPlanOffer,
  homeownerPlanChargeSentence,
  homeownerPlanCheckboxPlain,
  homeownerPlanCancelSentence,
  homeownerPlanButtonLabel,
  homeownerPlanNoticePlain,
  homeownerPlanConsentRow,
  homeownerSignupConsentRow,
};
